import React, { useMemo, useState } from "react";
import Dropzone from "react-dropzone";
import axios from "axios";
import LoadingOverlay from "../../components/LoadingOverlay";
import {
  CheckInShell,
  Icon,
  LegalNotice,
  SectionLabel,
  UploadZoneView,
} from "../../components/CheckInDesign";
import {
  ALLOWED_ID_IMAGE_ACCEPT,
  ID_IMAGE_ONLY_MESSAGE,
  isAllowedIdImageFile,
} from "../../components/uploadFileTypes";
import { compressImageForUploadSafe } from "../../components/imageCompression";
import { shouldAlwaysShowSuccessfulSubmit } from "../../components/featureFlags";
import { getPreferredIdUploadLanguage } from "../../components/languagePreference";
import { getUploadErrorMessage } from "../../components/userFacingErrors";

const REACT_APP_BASE_URL = process.env.REACT_APP_BASE_URL;

const getTestProgress = () => {
  /* istanbul ignore next -- browser-only scene; this guard protects non-DOM imports */
  if (typeof window === "undefined") return null;

  const parseProgress = (raw) => {
    if (raw === null) return null;
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const fromSearch = parseProgress(
    new URLSearchParams(window.location.search).get("testProgress")
  );
  if (fromSearch !== null) return fromSearch;

  const hash = window.location.hash || "";
  const hashQueryIndex = hash.indexOf("?");
  if (hashQueryIndex !== -1) {
    const fromHash = parseProgress(
      new URLSearchParams(hash.slice(hashQueryIndex)).get("testProgress")
    );
    if (fromHash !== null) return fromHash;
  }

  if (typeof window.__UPLOAD_TEST_PROGRESS__ === "number") {
    return window.__UPLOAD_TEST_PROGRESS__;
  }

  return null;
};

const isItalian = () => getPreferredIdUploadLanguage() === "it";

export default function Uploaded() {
  const testProgress = getTestProgress();
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState(testProgress !== null ? "loading" : null);
  const [uploadProgress, setUploadProgress] = useState(testProgress);
  const [retryFile, setRetryFile] = useState(null);

  const ctx = useMemo(() => {
    try {
      const raw = localStorage.getItem("idupload_ctx");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.confirmationCode) return null;
      if (parsed?.expiresAt) {
        const now = new Date();
        const exp = new Date(parsed.expiresAt);
        if (now > exp) return { ...parsed, expired: true };
      }
      return parsed;
    } catch {
      return null;
    }
  }, []);

  const onDrop = async (files) => {
    if (!files || files.length === 0) return;
    /* istanbul ignore next -- the dropzone is only rendered for valid, unexpired contexts */
    if (!ctx || ctx.expired) return;
    const file = files[0];
    const alwaysShowSuccess = shouldAlwaysShowSuccessfulSubmit();
    setStatus(null);
    setMessage("");
    setRetryFile(null);
    if (!isAllowedIdImageFile(file)) {
      setStatus("error");
      setMessage(
        isItalian()
          ? "Carica il documento come immagine."
          : ID_IMAGE_ONLY_MESSAGE
      );
      return;
    }
    setRetryFile(file);
    setStatus("loading");
    setUploadProgress(0);
    await Promise.resolve();
    const uploadFile = await compressImageForUploadSafe(file);
    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("confirmationCode", ctx.confirmationCode);
    axios
      .post(`${REACT_APP_BASE_URL}/idupload/upload-extra`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const total = progressEvent?.total;
          if (!total) return;
          const percent = Math.round((progressEvent.loaded * 100) / total);
          setUploadProgress(percent);
        },
      })
      .then((response) => {
        if (!alwaysShowSuccess && response.data?.received !== true) {
          setStatus("error");
          setUploadProgress(null);
          setMessage(getUploadErrorMessage(null, getPreferredIdUploadLanguage()));
          return;
        }
        setRetryFile(null);
        setStatus("success");
        setUploadProgress(null);
        setMessage("");
      })
      .catch((err) => {
        if (alwaysShowSuccess) {
          setRetryFile(null);
          setStatus("success");
          setUploadProgress(null);
          setMessage("");
          return;
        }
        setStatus("error");
        setUploadProgress(null);
        setMessage(getUploadErrorMessage(err, getPreferredIdUploadLanguage()));
      });
  };

  const title = isItalian()
    ? "Fatto. Altri ospiti? Carica qui i loro documenti"
    : "Done. More guests? Please upload here their ID";
  const subtitle = isItalian()
    ? "Se devi aggiungere ulteriori pagine, trascinale qui sotto."
    : "If you need to add additional pages, drop them below.";
  const dropzoneHint = isItalian()
    ? "Trascina qui un'immagine del documento, oppure fai clic per selezionare."
    : "Drag an image of your ID here, or click to select a file.";

  const uploadZoneState = "idle";

  return (
    <CheckInShell>
      <div className="v0-card-scroll" data-testid="uploaded-page">
        <LoadingOverlay
          loading={status === "loading"}
          message={
            isItalian() ? "Caricamento del documento..." : "Uploading document..."
          }
          progress={uploadProgress}
        />
        <div className="v0-card-stack">
          {ctx && !ctx.expired && status === "success" ? (
            <div className="v0-page-success-banner" role="status" aria-live="polite">
              <span className="v0-page-success-icon" aria-hidden="true">
                <Icon name="check" size={22} />
              </span>
              <div className="v0-page-success-copy">
                <p className="v0-page-success-text">
                  {isItalian() ? "Grazie" : "Thank you"}
                </p>
                <p className="v0-page-success-detail">
                  Documents received — you don&apos;t need to do anything else. Thank you.
                </p>
                <p className="v0-page-success-detail v0-page-success-text--secondary">
                  Documenti ricevuti, non serve fare altro. Grazie.
                </p>
              </div>
            </div>
          ) : null}

          {ctx && !ctx.expired && status === "error" && message ? (
            <div className="v0-page-error-banner" role="status" aria-live="polite">
              <span className="v0-page-error-icon" aria-hidden="true">
                <Icon name="info" size={22} />
              </span>
              <p className="v0-page-error-text">{message}</p>
              {retryFile ? (
                <button type="button" onClick={() => onDrop([retryFile])}>
                  {isItalian() ? "Riprova" : "Try again"}
                </button>
              ) : null}
            </div>
          ) : null}

          <div className="v0-hero-copy">
            <h1>{title}</h1>
            <p>{subtitle}</p>
          </div>

          {ctx && !ctx.expired ? (
            <>
              <div>
                <SectionLabel id="extra-upload-heading">
                  {isItalian() ? "Documento aggiuntivo" : "Additional ID or pages"}
                </SectionLabel>
                <Dropzone accept={ALLOWED_ID_IMAGE_ACCEPT} onDrop={onDrop} multiple={false}>
                  {({ getRootProps, getInputProps }) => (
                    <section>
                      <UploadZoneView
                        state={uploadZoneState}
                        previewUrl={null}
                        fileName={null}
                        message={dropzoneHint}
                        rootProps={getRootProps({
                          className: "dropzone",
                        })}
                        inputProps={getInputProps()}
                      />
                    </section>
                  )}
                </Dropzone>
              </div>

              <div>
                <LegalNotice />
              </div>
            </>
          ) : (
            <p className="v0-upload-copy">
              {isItalian()
                ? "Questo link non accetta più documenti aggiuntivi."
                : "This link is no longer accepting additional documents."}
            </p>
          )}
        </div>
      </div>
    </CheckInShell>
  );
}
